// Utils
import { PROPOSAL_ORIENTATION } from './constants';
import { COMPARISON_ITEM_TYPES } from '../../../utils/constants';
import { splitArrayInGroups } from '../../../utils/objects';
import CorrelationBoundaries from '@/types/correlation-boundaries';
import ProposalItem from '@/types/proposal-item';
import CorrelationPageType from '@/types/correlation-page-type';
import ProposalOrientation from '@/types/proposal-orientation';
import CorrelationMatrixData from '@/types/correlation-matrix-data';

export const PORTRAIT_LIMITS: CorrelationBoundaries = {
  MAX_INLINE: 5,
  MAX_MULTIPLE: 14,
  MAX_SINGLE: 33,
};

export const LANDSCAPE_LIMITS: CorrelationBoundaries = {
  MAX_INLINE: 5,
  MAX_MULTIPLE: 10,
  MAX_MULTIPLE_LARGE: 20,
  MAX_SINGLE: 25,
};

export const CORRELATION_PER_PAGE = {
  PORTRAIT: 4,
  LANDSCAPE: 6,
  LANDSCAPE_LARGE: 2,
};

export enum CORRELATION_MATRIX_LAYOUT {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
  MULTIPLE_LARGE = 'multipleLarge',
}

/**
 * Returns an array of CorrelationMatrixData based on group size and type
 * @param {ProposalItem[]} items array of items to be split in groups
 * @param {number} groupBy size of each group
 * @param {CorrelationPageType} type type of matrix page layout
 * @returns array of correlationMatrixData
 */
export const groupItemsForCorrelationData = (items: ProposalItem[], groupBy: number, type: CorrelationPageType) => {
  const groupedItems = splitArrayInGroups(items, groupBy);

  return groupedItems.map(group => ({
    items: group,
    type,
  }));
};

/**
 * Returns True if items length are under or equal to orientation threshold
 *
 * @param {ProposalItem[]} items List of the proposal items
 * @return {boolean} returns True if items length are below orientation threshold
 */
export const isCorrelationMatrixProposalInline = (items: ProposalItem[], orientation: ProposalOrientation) => {
  const boundaries = orientation === PROPOSAL_ORIENTATION.LANDSCAPE ? LANDSCAPE_LIMITS : PORTRAIT_LIMITS;
  return items.every(item => item.holdings.length <= boundaries.MAX_INLINE) && items.some(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);
};

/**
 * Returns a filtered array with only portfolio or group of holdings length below max threshold
 * @param {ProposalItem[]} items Proposal Items
 * @param {ProposalOrientation} orientation Proposal Orientation
 * @returns Array of items type portfolio or group
 */
export const getExceededItemsForCorrelation = (items: ProposalItem[], orientation: ProposalOrientation) => {
  const boundaries = orientation === PROPOSAL_ORIENTATION.LANDSCAPE ? LANDSCAPE_LIMITS : PORTRAIT_LIMITS;
  return items.filter(
    i =>
      (i.type === COMPARISON_ITEM_TYPES.PORTFOLIO || i.type === COMPARISON_ITEM_TYPES.GROUP) && i.holdings.length && i.holdings.length <= boundaries.MAX_SINGLE,
  );
};

/**
 * Returns an array of matrices that are displayed in groups according to provided limits
 * @param {number} lowerLimit Excluded lower limit
 * @param {number} upperLimit Included upper limit
 * @param {ProposalItem[]} items Proposal Items
 * @returns Array of grouped by page matrices
 */
export const getMatricesForPagination = (lowerLimit: number = 0, upperLimit: number, items: ProposalItem[]) =>
  items.filter(i => i.holdings.length > lowerLimit && i.holdings.length <= upperLimit);

/**
 * Returns an array of objects representing proposal pages
 * @param {ProposalOrientation} orientation Proposal Orientation
 * @param {ProposalItem[]} items Proposal items
 * @returns {CorrelationData[]} sliced array of correlation matrix pages
 */
export const splitProposalCorrelationMatrix = (orientation: ProposalOrientation, items: ProposalItem[]) => {
  if (!items.length || !orientation) {
    return [];
  }

  // filter for non-portfolio AND portfolio/group exceeding size limit
  const exceededItems = getExceededItemsForCorrelation(items, orientation);

  if (!exceededItems.length) {
    return [];
  }

  let data: CorrelationMatrixData[] = [];
  const landscape = orientation === PROPOSAL_ORIENTATION.LANDSCAPE;
  const boundaries = landscape ? LANDSCAPE_LIMITS : PORTRAIT_LIMITS;
  const multipleUpperLimit = boundaries.MAX_MULTIPLE_LARGE ? boundaries.MAX_MULTIPLE_LARGE : boundaries.MAX_MULTIPLE;

  const singleMatrixItems = getMatricesForPagination(multipleUpperLimit, boundaries.MAX_SINGLE, exceededItems);
  const multiMatrixItems = getMatricesForPagination(0, boundaries.MAX_MULTIPLE, exceededItems);

  if (singleMatrixItems.length) {
    data = data.concat(groupItemsForCorrelationData(singleMatrixItems, 1, CORRELATION_MATRIX_LAYOUT.SINGLE));
  }

  if (boundaries.MAX_MULTIPLE_LARGE) {
    const multiLargeMatrixItems = getMatricesForPagination(boundaries.MAX_MULTIPLE, boundaries.MAX_MULTIPLE_LARGE, exceededItems);
    const groupBy = landscape ? CORRELATION_PER_PAGE.LANDSCAPE_LARGE : CORRELATION_PER_PAGE.PORTRAIT;
    data = data.concat(groupItemsForCorrelationData(multiLargeMatrixItems, groupBy, CORRELATION_MATRIX_LAYOUT.MULTIPLE_LARGE));
  }

  if (multiMatrixItems.length) {
    const groupBy = landscape ? CORRELATION_PER_PAGE.LANDSCAPE : CORRELATION_PER_PAGE.PORTRAIT;
    data = data.concat(groupItemsForCorrelationData(multiMatrixItems, groupBy, CORRELATION_MATRIX_LAYOUT.MULTIPLE));
  }

  return data;
};
